import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Button, Container, Flex, HStack, Image, Show, Text, useMediaQuery } from '@chakra-ui/react'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { useDeleteApplyV2ProfileMutation } from '@core/app/api/applyJobV2Api'
import { useGetThemeQuery } from '@core/app/api/globalApi'
import {
    useGetPostInfoPreviewQuery,
    useGetPostInfoQuery,
    useGetPostSettingsQuery,
    useSetJobApplyCandidateMutation,
} from '@core/app/api/postsApi'
import { useGetHeaderInfoQuery } from '@core/app/api/profileApi'
import { setResetForm } from '@core/app/slices/profile'
import { Localization } from '@core/app/types/globalApiType'
import { IconBeingProccesed } from '@core/assets/icons/being_proccesed'
import { IconCalendar } from '@core/assets/icons/calendar'
import { IconDeclined } from '@core/assets/icons/declined'
import { IconEmployed } from '@core/assets/icons/employed'
import checked from '@core/assets/images/applicationExist.svg'
import employeeOnlyImage from '@core/assets/images/employee-only-img.svg'
import jobHasExpired from '@core/assets/images/job_has_expired.svg'
import noPostImage from '@core/assets/images/no-found-jp.svg'
import { AppButton } from '@core/components/AppButton'
import { Header } from '@core/components/Header/Header'
import { Layout } from '@core/components/Layout/Layout'
import { useIsApplyDialog } from '@core/utils/contexts/ApplyDialogContext/ApplDialogContext'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { ModalContext } from '@core/utils/contexts/ModalContext/ModalContext'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import { getLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'
import { useAppDispatch } from '@core/utils/hooks/hook'
import useIsEmployee from '@core/utils/hooks/useIsEmployee'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'

import { ApplyJobBlock } from '@page/post/components/ApplyJob/ApplyJob'
import { AsideInfo } from '@page/post/components/AsideInfo/AsideInfo'
import { PostInfoBlock } from '@page/post/components/PostInfo/PostInfo'
import inProgressIcon from '@page/posts/assets/images/progress_loader.svg'

import './PostPageNew.scss'

const baseClass = 'PostPageNew'

function PostPageNew() {
    const [localization, setLocalization] = useState<Localization>({})
    const isPreview = window.location.href.includes('job/preview')
    const dispatch = useAppDispatch()
    const [applyJob] = useSetJobApplyCandidateMutation()
    const [view, setView] = useState<string>('job')
    const { id } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const { isEmployee } = useIsEmployee()
    const applyBtnRef = useRef(null)
    const { open, collapseDialog, isApplyDialog, close, resized } = useIsApplyDialog()
    const [serviceCode] = useLocalStorageStore('service_code')
    const { onOpenApply } = useApplyV2Context()
    const applyVersion = searchParams.get('apply')
    const isApplyFormEnabled = searchParams.get('applyForm') === '1'
    const numberId = useMemo(() => {
        const n = Number(id)
        return isNaN(n) ? 0 : n
    }, [id])
    const { data: headerData } = useGetHeaderInfoQuery({}, { skip: !numberId })
    const candidateId = headerData?.data?.CandidateID ?? 0
    const applyV2Key = useMemo(() => `apply_dialog_v2-${numberId}-${candidateId}` as const, [numberId, candidateId])
    const [storageData, _, removeStorageData] = useLocalStorageStore(applyV2Key)
    const customPrefix: string = document.getElementById('__custom_route_data')?.getAttribute('data-prefix') ?? ''
    const [deleteApplyV2Profile, { isLoading: isProfileDeleteLoading }] = useDeleteApplyV2ProfileMutation()
    const {
        data: postInfo,
        isLoading,
        isError,
        error: postInfoError,
    } = isPreview
        ? useGetPostInfoPreviewQuery({ getParams: window.location.search })
        : useGetPostInfoQuery({ id: numberId }, { skip: !numberId })

    const {
        data: settings,
        isSuccess,
        isLoading: isSettingsLoading,
        isError: isSettingsError,
    } = useGetPostSettingsQuery(
        // @ts-ignore
        { id, code: serviceCode },
        { skip: id === undefined }
    )
    const [isDesktop] = useMediaQuery('(min-width: 1230px)', {
        fallback: false,
    })
    const { t } = useTranslation()
    const { openModal, closeModal } = useContext(ModalContext)
    const { appTheme, updateTheme, isExpired } = useAppTheme()
    const bg = appTheme?.newSettings?.general?.Background ? `#${appTheme?.newSettings?.general?.Background}` : 'bg.3'

    const isApplyOnly = location.href.includes('apply_only')
    const currentJobId = getLocalStorageItem('apply_dialog')?.id
    // const [storageData, setStorageData, removeStorageData] = useLocalStorageStore(`apply_dialog_v2-${numberId}-${0}`)

    const token = searchParams.get('preview_token') || undefined
    const { data: mainTheme } = useGetThemeQuery({ preview_token: token }, { skip: isExpired && !token })

    const today = new Date()
    const postDate = new Date(postInfo?.data?.ad?.Expire || '')
    const isPostEnded = postInfo?.data?.ad?.Ended || false
    const isPostExpired = postDate.getTime() < today.getTime() && postDate.getDate() !== today.getDate()
    const isPostUnavailable = isPostEnded || isPostExpired

    const isEmployeesOnly = useMemo(() => {
        return postInfo?.data?.ad?.EmployeesOnly
    }, [postInfo?.data?.ad?.EmployeesOnly])

    const isNewApplyEnabled =
        // = true
        useMemo(() => {
            const domFlag =
                ((document?.querySelector('input[name="__new_apply_form_enabled"]') as HTMLInputElement)?.value ??
                    '') === '1'
            return Boolean(postInfo?.data?.isNewApplyEnabled ?? domFlag)
        }, [postInfo?.data?.isNewApplyEnabled])

    const autoOpenedRef = useRef(false)

    const settingsLoaded = !isSettingsLoading && (isSuccess || isSettingsError)

    const months = [
        `${t('489')}`,
        `${t('490')}`,
        `${t('491')}`,
        `${t('492')}`,
        `${t('493')}`,
        `${t('494')}`,
        `${t('495')}`,
        `${t('496')}`,
        `${t('497')}`,
        `${t('498')}`,
        `${t('499')}`,
        `${t('500')}`,
    ]

    const isOS = () => {
        const userAgent = navigator.userAgent
        if (/iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
            return 'ios'
        }
        return 'unknown'
    }

    useEffect(() => {
        if (mainTheme) {
            updateTheme(mainTheme)
        }
    }, [mainTheme])

    const isApplyShowed =
        isSuccess &&
        settings.application &&
        Object.keys(settings?.application || {}).length === 0 &&
        (!resized || !isApplyDialog)

    const getDifference = (expireDate: string) => {
        const date1: Date = new Date(expireDate)
        const date2: Date = new Date()

        // @ts-ignore
        const diffInMs = Math.abs(date2 - date1)

        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

        if (diffInDays === 1) {
            return `(${t('2839')})`
        } else {
            return ''
        }
    }

    const submitHandler = async () => {
        if (id) {
            try {
                await applyJob({
                    id,
                    data: {
                        'g-recaptcha-response': '',
                    },
                }).unwrap()

                close()
                localStorage.setItem('after_apply', '1')
                localStorage.setItem('after_apply_id', id)
                localStorage.setItem('after_apply_title', decodeHtmlEntitiesRecursive(postInfo?.data?.ad?.JobTitle))
                location.href = '/profile'
            } catch (error: any) {}
        }
    }

    useEffect(() => {
        if (isApplyOnly && isNewApplyEnabled && id && postInfo && !isApplyDialog && !autoOpenedRef.current) {
            autoOpenedRef.current = true
            onOpenApply()
        }
    }, [isApplyOnly, isNewApplyEnabled, id, postInfo, isApplyDialog, onOpenApply])

    const openApplyWindowHandler = () => {
        if (settings?.externalLink && settings?.externalLink?.length > 0) {
            const externalLink = settings.externalLink
            if (externalLink.startsWith('mailto:')) window.open(settings?.externalLink, '_blank')
            else {
                const link = settings?.externalLink.includes('http')
                    ? settings?.externalLink
                    : `https://${settings?.externalLink}`
                window.open(link, '_blank')?.focus()
            }
            return
        }

        // TODO -start- Apply v2
        if (applyVersion === 'v2' || isNewApplyEnabled) {
            onOpenApply()
            return
        }
        // TODO -end- Apply v2

        if (
            !settings?.externalAuth &&
            !settings?.askAttributes &&
            settings?.screeningQuestions?.length === 0 &&
            !settings.settings?.anonymousApplication &&
            // !settings.requiredFields?.applicationText &&
            !settings.fields?.applicationText.visible &&
            settings.isUser
        ) {
            submitHandler()
            return
        }

        if (isApplyDialog) {
            if (id !== currentJobId) {
                openModal({
                    size: 'md',
                    isHeaderVisible: false,
                    isFooterVisible: false,
                    body: (
                        <Box p={6}>
                            <Text fontSize={'2xl'} fontWeight={'semibold'} color={'text.main'}>
                                {t('7399')}
                            </Text>
                            <Text color={'text.secondary'} mt={4}>
                                {t('7400')} {t('7401')},{' '}
                                <Text display={'inline'} fontWeight={'semibold'}>
                                    {t('7402')}
                                </Text>
                            </Text>
                            <Text color={'text.secondary'} mt={4}>
                                {t('7403')}
                            </Text>
                            <Flex
                                w={'full'}
                                flexDirection={'column'}
                                gap={3}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                mt={'40px'}
                            >
                                <Button
                                    variant={'error'}
                                    fontSize={'sm'}
                                    w={'full'}
                                    ra-data-job-post-id={id}
                                    id={'ra-button-job-post-action-application-form-close-ok'}
                                    onClick={() => {
                                        dispatch(setResetForm(true))

                                        setTimeout(() => {
                                            closeModal()
                                            localStorage.removeItem('mobile_verification')
                                        }, 0)

                                        open(id ? id : '', {
                                            title: decodeHtmlEntitiesRecursive(postInfo?.data?.ad?.JobTitle),
                                            link: `/job/${id}`,
                                            expiredDate: postInfo?.data?.ad?.Expire || '',
                                        })
                                    }}
                                >
                                    {t('7404')}
                                </Button>
                                <Button w={'full'} variant={'secondary'} onClick={closeModal}>
                                    {t('7405')}
                                </Button>
                            </Flex>
                        </Box>
                    ),
                })
            } else {
                collapseDialog()
            }
        } else {
            open(id ? id : '', {
                title: decodeHtmlEntitiesRecursive(postInfo?.data?.ad?.JobTitle),
                link: `/job/${id}`,
                expiredDate: postInfo?.data?.ad.Expire || '',
            })
        }
    }

    useEffect(() => {
        if (isApplyFormEnabled && settings) {
            openApplyWindowHandler()

            const params = new URLSearchParams(searchParams)
            params.delete('applyForm')

            setSearchParams(params, { replace: true })
        } else if (isApplyFormEnabled && storageData?.postInitUid && !settings) {
            deleteApplyV2Profile({ uid: storageData.postInitUid })
            removeStorageData()
        }
    }, [isApplyFormEnabled, settings, openApplyWindowHandler, searchParams, setSearchParams, storageData])

    useEffect(() => {
        const path = searchParams.get('path')
        const service_code = searchParams.get('code')

        if (path) {
            localStorage.setItem('apply_path', path)
        }

        if (service_code && id) {
            open(id, {
                title: decodeHtmlEntitiesRecursive(postInfo?.data?.ad?.JobTitle),
                link: `/job/${id}`,
                expiredDate: postInfo?.data?.ad?.Expire || '',
            })
        }
    }, [searchParams])

    if (isApplyOnly && !isNewApplyEnabled && !settingsLoaded) {
        return (
            <Layout showFooter={false} showHeader={false} useContainer={isDesktop} useMobileHeader={false}>
                <Flex minH="100vh" align="center" justify="center" bg={bg}>
                    <Text fontWeight="medium">{t('loading', 'Loading...')}</Text>
                </Flex>
            </Layout>
        )
    }

    if (postInfoError?.data?.reason === 'employeesOnly') {
        return (
            <Flex minH={'100vh'} flexDirection={'column'} bg={bg}>
                <Header />
                <Flex
                    as={'main'}
                    flexDirection={'column'}
                    w={'full'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    h={'full'}
                    flex={1}
                >
                    <Flex w={'336px'} flexDirection={'column'} alignItems={'center'} h={'full'}>
                        <Image src={employeeOnlyImage} alt={`${t('7841')}`} />
                        <Text fontSize={'24px'} fontWeight={'semibold'} lineHeight={'32px'} textAlign={'center'} mt={6}>
                            {t('11237', 'Employees only')}
                        </Text>
                        <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'} mt={2}>
                            {t('11238', 'This job post is visible to employees only.')}
                        </Text>
                        <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'}>
                            {t('11239', 'Please log in to continue.')}
                        </Text>
                        <AppButton
                            translate="no"
                            title={t('1890', 'Log in')}
                            variant={'primary'}
                            w={'184px'}
                            mt={6}
                            onClick={() => (location.href = '/login')}
                        />
                    </Flex>
                </Flex>
            </Flex>
        )
    }

    if (settingsLoaded && isApplyOnly && !isNewApplyEnabled && !settings?.isUser) {
        return (
            <>
                {!isError && !isSettingsError ? (
                    <Layout
                        showFooter={false}
                        showHeader={false}
                        useContainer={isDesktop ? true : false}
                        useMobileHeader={false}
                    >
                        <Flex minH={'100vh'} flexDirection={'column'} bg={bg}>
                            <Flex
                                as={'main'}
                                flexDirection={'column'}
                                w={'full'}
                                alignItems={'center'}
                                justifyContent={'center'}
                                h={'full'}
                                flex={1}
                                py={'80px'}
                            >
                                <Container maxW={{ base: 'container.sm', xl: 'container.xl' }}>
                                    <Flex flexDirection={'column'} gap={'64px'} w={'full'} alignItems={'center'}>
                                        {appTheme?.headerLogo && (
                                            <Image
                                                maxW={{ base: '160px', xl: '200px' }}
                                                maxH={'80px'}
                                                src={appTheme?.headerLogo}
                                                alt={`${t('7840')}`}
                                            />
                                        )}
                                        <Box
                                            boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                                            w={{ base: 'auto', xl: '800px' }}
                                            p={4}
                                            borderRadius={'8px'}
                                            bg={'bg.1'}
                                        >
                                            <ApplyJobBlock />
                                        </Box>
                                    </Flex>
                                </Container>
                            </Flex>
                        </Flex>
                    </Layout>
                ) : (
                    <Flex minH={'100vh'} flexDirection={'column'} bg={bg}>
                        <Header />
                        <Flex
                            as={'main'}
                            flexDirection={'column'}
                            w={'full'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            h={'full'}
                            flex={1}
                        >
                            <Flex w={'336px'} flexDirection={'column'} alignItems={'center'} h={'full'}>
                                <Image src={noPostImage} alt={`${t('7841')}`} />
                                <Text
                                    fontSize={'24px'}
                                    fontWeight={'semibold'}
                                    lineHeight={'32px'}
                                    textAlign={'center'}
                                    mt={6}
                                >
                                    {t('6924', 'Not found')}
                                </Text>
                                <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'} mt={2}>
                                    {t('6925', 'The job post no longer exists')}
                                </Text>
                                <AppButton
                                    translate="no"
                                    title={t('6926')}
                                    variant={'primary'}
                                    w={'184px'}
                                    mt={6}
                                    onClick={() => (location.href = '/jobs')}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                )}
            </>
        )
    } else {
        return (
            <>
                {!isError ? (
                    <>
                        <Layout useContainer={isDesktop} useMobileHeader={false}>
                            <Box
                                className={`${baseClass}__section__box`}
                                as={'section'}
                                pt={{ base: 2, xl: 10 }}
                                pb={{ base: '80px', xl: 10 }}
                            >
                                <Flex
                                    justifyContent={'space-between'}
                                    className={`${baseClass}__section__box--flex--space-between`}
                                >
                                    <Box
                                        className={`${baseClass}__section__box--flex--space-between--box`}
                                        borderRadius={'8px'}
                                        // w={{ base: '100%', xl: '800px' }}
                                    >
                                        {isEmployeesOnly && isPreview && (
                                            <Box
                                                bg={'bg.1'}
                                                borderRadius={{
                                                    base: '0px',
                                                    xl: '8px',
                                                }}
                                                // py={{ base: '18px', xl: '22px' }}
                                                // px={{ base: 4, xl: 6 }}
                                                // mb={{ base: 2, xl: 4 }}
                                                padding={'24px'}
                                                marginBottom={'10px'}
                                                boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                                                display={'flex'}
                                                // alignItems={'center'}
                                                flexDirection={'column'}
                                                // flexDirection={{ base: 'column', md: 'row' }}
                                                gap={'6px'}
                                            >
                                                <Flex alignItems={'center'} gap={'8px'}>
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 20 20"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            clipRule="evenodd"
                                                            d="M7.70833 5C7.70833 3.73435 8.73435 2.70833 10 2.70833C11.2657 2.70833 12.2917 3.73435 12.2917 5V7.58341H7.70833V5ZM5.625 7.58341V5C5.625 2.58375 7.58375 0.625 10 0.625C12.4162 0.625 14.375 2.58375 14.375 5V7.58341H15.1849C16.4979 7.58341 17.4163 8.75088 17.4163 10.0001V16.6667C17.4163 17.9159 16.4979 19.0834 15.1849 19.0834H4.81449C3.50147 19.0834 2.58301 17.9159 2.58301 16.6667V10.0001C2.58301 8.75088 3.50147 7.58341 4.81449 7.58341H5.625ZM9.9997 11.6668C10.4599 11.6668 10.833 12.0399 10.833 12.5001V14.1668C10.833 14.627 10.4599 15.0001 9.9997 15.0001C9.53946 15.0001 9.16636 14.627 9.16636 14.1668V12.5001C9.16636 12.0399 9.53946 11.6668 9.9997 11.6668Z"
                                                            fill="#CC8B00"
                                                        />
                                                    </svg>
                                                    <Text color={'#996800'} fontSize={'16px'} fontWeight={'600'}>
                                                        {t('11237', 'Employees only')}
                                                    </Text>
                                                </Flex>
                                                <Text
                                                    color={'#4B4B4E'}
                                                    fontSize={'14px'}
                                                    letterSpacing={'0.16px'}
                                                    fontWeight={'500'}
                                                >
                                                    {t(
                                                        '11240',
                                                        'This job posting is visible only to employees who are logged in'
                                                    )}
                                                    {'. '}
                                                    {t('11241', 'External candidates will not see this posting')}
                                                    {'. '}
                                                </Text>
                                            </Box>
                                        )}
                                        {isPostUnavailable && (
                                            <Box
                                                bg={'bg.1'}
                                                borderRadius={{
                                                    base: '0px',
                                                    xl: '8px',
                                                }}
                                                py={{ base: '18px', xl: '22px' }}
                                                px={{ base: 4, xl: 6 }}
                                                mb={{ base: 2, xl: 4 }}
                                                boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                                                display={'flex'}
                                                alignItems={'center'}
                                                flexDirection={{ base: 'column', md: 'row' }}
                                                gap={'20px'}
                                            >
                                                <Image src={jobHasExpired} width={'72px'} alt={`${t('4720')}`} />
                                                <Flex flexDirection={'column'} flexGrow={1}>
                                                    <Text
                                                        color={'#ba5b1c'}
                                                        display={'inline-flex'}
                                                        gap={'8px'}
                                                        alignItems={'center'}
                                                        fontWeight={'600'}
                                                    >
                                                        <i className="fa-solid fa-triangle-exclamation"></i>
                                                        {isPostEnded
                                                            ? t('9973', 'Job has been archived')
                                                            : t('8727', 'Job has expired')}
                                                    </Text>
                                                    <Text fontSize={'14px'} fontWeight={'500'}>
                                                        {isPostEnded
                                                            ? `${t(
                                                                  '9974',
                                                                  'This job posting has been archived and you can no longer apply'
                                                              )}.`
                                                            : t(
                                                                  '8728',
                                                                  'This job posting has expired and you can no longer apply.'
                                                              )}
                                                    </Text>
                                                    <Text fontSize={'14px'} fontWeight={'500'}>
                                                        {t('8729', 'Click the button to view all available jobs.')}
                                                    </Text>
                                                </Flex>
                                                <AppButton
                                                    translate="no"
                                                    variant={'primary'}
                                                    minW={'132px'}
                                                    mt={6}
                                                    onClick={() => (location.href = '/jobs')}
                                                >
                                                    {t('8730', 'View all jobs')}
                                                </AppButton>
                                            </Box>
                                        )}
                                        <Box className={`${baseClass}__section__box--flex--space-between--1`}>
                                            {isSuccess &&
                                                settings.application &&
                                                Object.keys(settings.application || {}).length > 0 && (
                                                    <Box
                                                        className={`${baseClass}__section__box--flex--space-between--isSuccess`}
                                                        key={settings.application?.AdID}
                                                        bg={'bg.1'}
                                                        borderRadius={{
                                                            base: '0px',
                                                            xl: '8px',
                                                        }}
                                                        py={{ base: '18px', xl: '22px' }}
                                                        px={{ base: 4, xl: 6 }}
                                                        mb={{ base: 2, xl: 4 }}
                                                        boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                                                    >
                                                        {settings.application?.Active === 1 ? (
                                                            <Flex gap={'2'}>
                                                                <Image src={checked} maxW={4} alt={`${t('2450')}`} />
                                                                <Text fontWeight={'semibold'} color={'text.success'}>
                                                                    {t('2450')}
                                                                </Text>
                                                            </Flex>
                                                        ) : (
                                                            <Flex gap={'2'}>
                                                                <Image
                                                                    src={inProgressIcon}
                                                                    maxW={4}
                                                                    alt={`${t('4720')}`}
                                                                />
                                                                <Text fontWeight={'semibold'} color={'text.warning'}>
                                                                    {t('4720')}
                                                                </Text>
                                                            </Flex>
                                                        )}
                                                        {settings.application?.ApplicationText && (
                                                            <Box mt={5}>
                                                                <Text fontSize={'sm'} color={'text.main'}>
                                                                    {t('1221')}
                                                                </Text>
                                                                <Box
                                                                    className="PostPageNew__applicationText"
                                                                    fontSize={'sm'}
                                                                    color={'text.secondary'}
                                                                    mt={2}
                                                                >
                                                                    {parse(
                                                                        decodeHtmlEntitiesRecursive(
                                                                            settings.application.ApplicationText
                                                                        )
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                        <Flex
                                                            justifyContent={'space-between'}
                                                            alignItems={'center'}
                                                            mt={5}
                                                        >
                                                            {settings.application.Applied !== '0000-00-00 00:00:00' && (
                                                                <HStack>
                                                                    <IconCalendar />
                                                                    <Text fontSize={'sm'} color={'text.thirdly'}>
                                                                        {t('1215')}:
                                                                    </Text>
                                                                    <Text fontSize={'sm'} color={'text.thirdly'}>
                                                                        {`${
                                                                            months[
                                                                                +settings.application?.Applied?.split(
                                                                                    ' '
                                                                                )[0].split('-')[1] - 1
                                                                            ]
                                                                        } ${+settings.application?.Applied?.split(
                                                                            ' '
                                                                        )[0].split('-')[2]} ${getDifference(
                                                                            settings.application?.Applied?.split(' ')[0]
                                                                        )}`}
                                                                    </Text>
                                                                </HStack>
                                                            )}

                                                            <Box>
                                                                {settings.application?.ApplicationStatusID === 0 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <IconDeclined />
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('1579', 'Declined')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                                {settings.application?.ApplicationStatusID === 1 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <svg
                                                                            width="16"
                                                                            height="16"
                                                                            viewBox="0 0 16 16"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <g clipPath="url(#clip0_41625_108568)">
                                                                                <path
                                                                                    fillRule="evenodd"
                                                                                    clipRule="evenodd"
                                                                                    d="M1.9334 7.99909C1.9334 4.64856 4.64954 1.93242 8.00006 1.93242C11.3506 1.93242 14.0667 4.64856 14.0667 7.99909C14.0667 11.3496 11.3506 14.0658 8.00006 14.0658C4.64954 14.0658 1.9334 11.3496 1.9334 7.99909ZM8.00006 0.732422C3.9868 0.732422 0.733398 3.98582 0.733398 7.99909C0.733398 12.0124 3.9868 15.2658 8.00006 15.2658C12.0133 15.2658 15.2667 12.0124 15.2667 7.99909C15.2667 3.98582 12.0133 0.732422 8.00006 0.732422ZM11.0836 6.76398C11.3219 6.53376 11.3285 6.15392 11.0983 5.91558C10.8681 5.67724 10.4882 5.67065 10.2499 5.90087L7.21575 8.83156L5.75024 7.41602C5.5119 7.1858 5.13206 7.19239 4.90184 7.43073C4.67163 7.66907 4.67821 8.04891 4.91655 8.27913L6.79891 10.0973C7.03143 10.3219 7.40008 10.3219 7.6326 10.0973L11.0836 6.76398Z"
                                                                                    fill="#2F80ED"
                                                                                />
                                                                            </g>
                                                                            <defs>
                                                                                <clipPath id="clip0_41625_108568">
                                                                                    <rect
                                                                                        width="16"
                                                                                        height="16"
                                                                                        fill="white"
                                                                                    />
                                                                                </clipPath>
                                                                            </defs>
                                                                        </svg>
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('1215', 'Applied')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                                {settings.application?.ApplicationStatusID === 2 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <IconBeingProccesed />
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('6826', 'Being processed')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                                {settings.application?.ApplicationStatusID === 3 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <IconEmployed />
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('43', 'Employed')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                                {settings.application?.ApplicationStatusID === 4 && (
                                                                    <HStack alignItems={'center'} ml={'auto'}>
                                                                        <svg
                                                                            width="16"
                                                                            height="16"
                                                                            viewBox="0 0 16 16"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                clipRule="evenodd"
                                                                                d="M5.60851 1.89954C5.84759 2.12899 5.85539 2.50881 5.62593 2.74789L2.74088 5.75393H11.0108C13.3613 5.75393 15.2667 7.65937 15.2667 10.0098C15.2667 12.3603 13.3613 14.2658 11.0108 14.2658H9.75445C9.42308 14.2658 9.15445 13.9971 9.15445 13.6658C9.15445 13.3344 9.42308 13.0658 9.75445 13.0658H11.0108C12.6986 13.0658 14.0667 11.6976 14.0667 10.0098C14.0667 8.32211 12.6986 6.95393 11.0108 6.95393H2.74088L5.62593 9.95997C5.85539 10.199 5.84759 10.5789 5.60851 10.8083C5.36943 11.0378 4.98962 11.03 4.76016 10.7909L0.900513 6.76939C0.677694 6.53723 0.677694 6.17063 0.900513 5.93847L4.76016 1.91696C4.98962 1.67788 5.36943 1.67008 5.60851 1.89954Z"
                                                                                fill="#FFAE00"
                                                                            />
                                                                        </svg>
                                                                        <Text
                                                                            fontSize={'sm'}
                                                                            fontWeight={'medium'}
                                                                            color={'text.thirdly'}
                                                                        >
                                                                            {t('10181', 'Withdrawn')}
                                                                        </Text>
                                                                    </HStack>
                                                                )}
                                                            </Box>
                                                        </Flex>
                                                    </Box>
                                                )}
                                            <Show breakpoint={'(max-width: 1279px)'}>
                                                <Flex w={'full'} className={`${baseClass}__job_about`}>
                                                    <Box
                                                        bg={'bg.1'}
                                                        p={3}
                                                        flex={1}
                                                        borderBottom={view === 'job' ? '2px solid' : '1px solid'}
                                                        borderColor={view === 'job' ? 'state.primary' : 'border.4'}
                                                        onClick={() => {
                                                            setView('job')
                                                        }}
                                                        cursor={'pointer'}
                                                    >
                                                        <Text fontWeight={'semibold'} textAlign={'center'}>
                                                            {isApplyDialog ? `${t('1589')}` : `${t('730')}`}
                                                        </Text>
                                                    </Box>
                                                    <Box
                                                        bg={'bg.1'}
                                                        p={3}
                                                        flex={1}
                                                        borderBottom={view === 'about' ? '2px solid' : '1px solid'}
                                                        borderColor={view === 'about' ? 'state.primary' : 'border.4'}
                                                        onClick={() => setView('about')}
                                                        cursor={'pointer'}
                                                    >
                                                        <Text fontWeight={'semibold'} textAlign={'center'}>
                                                            {t('2492')}
                                                        </Text>
                                                    </Box>
                                                </Flex>
                                            </Show>
                                            <Show breakpoint={'(max-width: 1279px)'}>
                                                {view === 'job' ? (
                                                    <div className={`${baseClass}__PostInfoWrap__1`}>
                                                        <PostInfoBlock
                                                            data={postInfo?.data}
                                                            loaded={!isLoading}
                                                            isPostExpired={isPostUnavailable}
                                                            isPostArchived={isPostEnded}
                                                        />
                                                    </div>
                                                ) : (
                                                    <AsideInfo
                                                        data={postInfo?.data}
                                                        loaded={!isLoading}
                                                        isPostExpired={isPostUnavailable}
                                                        changeView={() => {
                                                            id &&
                                                                open(id, {
                                                                    title: decodeHtmlEntitiesRecursive(
                                                                        postInfo?.data?.ad?.JobTitle
                                                                    ),
                                                                    link: `/job/${id}`,
                                                                    expiredDate: postInfo?.data?.ad?.Expire || '',
                                                                })
                                                        }}
                                                    />
                                                )}
                                            </Show>
                                            {/*<Show above={'xl'}>*/}
                                            <div className={`${baseClass}__PostInfoWrap__2 ${baseClass}--above-xl`}>
                                                <PostInfoBlock
                                                    data={postInfo?.data}
                                                    loaded={!isLoading}
                                                    isPostExpired={isPostUnavailable}
                                                    isPostArchived={isPostEnded}
                                                />
                                            </div>
                                            {/*</Show>*/}
                                        </Box>
                                    </Box>
                                    {/*<Show above={'xl'}>*/}
                                    <div className={`${baseClass}__AsideInfoWrap ${baseClass}--above-xl`}>
                                        <Box borderRadius={'8px'} w={'384px'}>
                                            <AsideInfo
                                                data={postInfo?.data}
                                                loaded={!isLoading}
                                                isPostExpired={isPostUnavailable}
                                                changeView={() =>
                                                    id &&
                                                    open(id, {
                                                        title: decodeHtmlEntitiesRecursive(
                                                            postInfo?.data?.ad?.JobTitle
                                                        ),
                                                        link: `/job/${id}`,
                                                        expiredDate: postInfo?.data?.ad?.Expire || '',
                                                    })
                                                }
                                            />
                                        </Box>
                                    </div>
                                    {/*</Show>*/}
                                </Flex>
                            </Box>
                        </Layout>
                        <Show breakpoint="(max-width: 1279px)">
                            {isApplyShowed && !storageData && (
                                <Box
                                    className={`${baseClass}__bottomApplyBox`}
                                    position={'fixed'}
                                    w={'full'}
                                    bottom={0}
                                    left={0}
                                    right={0}
                                    py={2}
                                    px={4}
                                    pt={4}
                                    pb={isOS() === 'ios' ? '32px' : 4}
                                    bg={'bg.1'}
                                    transform={'translate3d(0,0,0)'}
                                    zIndex={'sticky'}
                                    borderTop={'1px solid'}
                                    borderColor={'border.1'}
                                >
                                    <AppButton
                                        ref={applyBtnRef}
                                        className="PostPage--apply-btn"
                                        translate="no"
                                        variant={'primary'}
                                        title={
                                            localization[30]
                                                ? localization[30]
                                                : t('6634', 'Apply now')
                                                      .toLowerCase()
                                                      .replace(/^\w/, (c) => c.toUpperCase())
                                        }
                                        ra-data-job-post-id={id}
                                        id={'ra-button-job-post-action-apply-now'}
                                        w={'full'}
                                        onClick={() => id && openApplyWindowHandler()}
                                    />
                                </Box>
                            )}
                        </Show>
                    </>
                ) : (
                    <Flex minH={'100vh'} flexDirection={'column'} bg={bg}>
                        <Header />
                        <Flex
                            as={'main'}
                            flexDirection={'column'}
                            w={'full'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            h={'full'}
                            flex={1}
                        >
                            <Flex w={'336px'} flexDirection={'column'} alignItems={'center'} h={'full'}>
                                <Image src={noPostImage} alt={`${t('7841')}`} />
                                <Text
                                    fontSize={'24px'}
                                    fontWeight={'semibold'}
                                    lineHeight={'32px'}
                                    textAlign={'center'}
                                    mt={6}
                                >
                                    {t('6924', 'Not found')}
                                </Text>
                                <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'} mt={2}>
                                    {t('6925', 'The job post no longer exists')}
                                </Text>
                                <AppButton
                                    translate="no"
                                    title={t('6926')}
                                    variant={'primary'}
                                    w={'184px'}
                                    mt={6}
                                    onClick={() => (location.href = '/jobs')}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                )}
            </>
        )
    }
}

export default PostPageNew
